// In-memory evaluation over wire-format documents: apply a write (sentinels included) and run a
// query (filters, order, cursors, limit, near). Clients use it for the offline cache and latency
// compensation; the server's realtime sessions use it to skip re-reads a change can't affect.
// Mirrors the SQL engine's semantics (packages/server/src/compile.ts): keep them in step.
import { withImplicitOrder, type DocOut, type Filter, type Query, type Write } from "./query.ts";

const isObj = (x: unknown): x is Record<string, unknown> => typeof x === "object" && x !== null && !Array.isArray(x);
const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
const isSentinel = (v: unknown): v is { $op: string; by?: number; values?: unknown[] } => isObj(v) && typeof v.$op === "string";

/** The value at a dotted path ("stats.plays"), or undefined. */
export function getPath(o: Record<string, unknown>, path: string): unknown {
  let cur: unknown = o;
  for (const k of path.split(".")) cur = isObj(cur) ? cur[k] : undefined;
  return cur;
}

function setPath(o: Record<string, unknown>, path: string, v: unknown) {
  const segs = path.split(".");
  let cur = o;
  for (const k of segs.slice(0, -1)) {
    if (!isObj(cur[k])) cur[k] = {};
    cur = cur[k] as Record<string, unknown>;
  }
  const last = segs[segs.length - 1]!;
  if (v === undefined || v === null) delete cur[last];
  else cur[last] = v;
}

/** A doc's data after a write, or null after a delete. `now` stands in for serverTimestamp. Keys may be dotted. */
export function applyWrite(before: Record<string, unknown> | null, w: Write, now: number): Record<string, unknown> | null {
  if (w.op === "delete") return null;
  if (w.op === "check") return before;
  const out: Record<string, unknown> = w.op === "set" && !w.merge ? {} : structuredClone(before ?? {});
  for (const [k, v] of Object.entries(w.data)) {
    if (!isSentinel(v)) {
      setPath(out, k, v);
      continue;
    }
    const cur = getPath(out, k);
    if (v.$op === "delete") setPath(out, k, undefined);
    else if (v.$op === "now") setPath(out, k, { $t: now });
    else if (v.$op === "inc") setPath(out, k, (typeof cur === "number" ? cur : 0) + (v.by ?? 0));
    else if (v.$op === "union") {
      const arr = Array.isArray(cur) ? [...cur] : [];
      for (const e of v.values ?? []) if (!arr.some((x) => same(x, e))) arr.push(e);
      setPath(out, k, arr);
    } else if (v.$op === "remove") setPath(out, k, (Array.isArray(cur) ? cur : []).filter((x) => !(v.values ?? []).some((r) => same(x, r))));
  }
  return out;
}

/** Comparable form of a wire value (timestamps → ms, refs → path). */
const key = (v: unknown): unknown => (isObj(v) && typeof v.$t === "number" ? v.$t : isObj(v) && typeof v.$ref === "string" ? v.$ref : typeof v === "boolean" ? (v ? 1 : 0) : v);

function cmp(a: unknown, b: unknown): number | null {
  const x = key(a), y = key(b);
  if (typeof x === "number" && typeof y === "number") return x - y;
  if (typeof x === "string" && typeof y === "string") return x < y ? -1 : x > y ? 1 : 0;
  return null;
}

const fieldOf = (d: DocOut, f: string) => (f === "id" || f === "__name__" ? d.id : getPath(d.data, f));

export function matchesFilter(d: DocOut, f: Filter | undefined): boolean {
  if (!f) return true;
  if ("and" in f) return f.and.every((x) => matchesFilter(d, x));
  if ("or" in f) return f.or.some((x) => matchesFilter(d, x));
  const v = fieldOf(d, f.field);
  const missing = v === undefined || v === null;
  const eq = (a: unknown, b: unknown) => same(key(a), key(b));
  switch (f.op) {
    case "==":
      return f.value === null ? missing : !missing && eq(v, f.value);
    case "!=":
      return f.value === null ? !missing : !missing && !eq(v, f.value);
    case "<":
    case "<=":
    case ">":
    case ">=": {
      if (missing) return false;
      const c = cmp(v, f.value);
      if (c === null) return false;
      return f.op === "<" ? c < 0 : f.op === "<=" ? c <= 0 : f.op === ">" ? c > 0 : c >= 0;
    }
    case "in":
      return !missing && Array.isArray(f.value) && f.value.some((x) => eq(v, x));
    case "not-in":
      return !missing && Array.isArray(f.value) && !f.value.some((x) => eq(v, x));
    case "array-contains":
      return Array.isArray(v) && v.some((x) => eq(x, f.value));
    case "array-contains-any":
      return Array.isArray(v) && Array.isArray(f.value) && v.some((x) => (f.value as unknown[]).some((y) => eq(x, y)));
  }
  return false;
}

const haversineMi = (a: number, b: number, c: number, d: number) => {
  const r = (x: number) => (x * Math.PI) / 180;
  const h = Math.sin(r(c - a) / 2) ** 2 + Math.cos(r(a)) * Math.cos(r(c)) * Math.sin(r(d - b) / 2) ** 2;
  return 3958.8 * 2 * Math.asin(Math.min(1, Math.sqrt(h)));
};

/** Is this document in the query's collection (path or group)? */
export function inCollection(d: DocOut, q: Query): boolean {
  const segs = d.path.split("/");
  if (q.group !== undefined) return segs[segs.length - 2] === q.group;
  return segs.slice(0, -1).join("/") === q.from;
}

const orderKeys = (q: Query) => {
  const orders = q.orderBy ?? [];
  return orders.some((o) => o.field === "id" || o.field === "__name__") ? orders : [...orders, { field: "id", dir: orders.at(-1)?.dir ?? "asc" }];
};

/** Compares two docs in the query's result order (orderBy, then id). */
export function orderComparator(query: Query): (a: DocOut, b: DocOut) => number {
  const keys = orderKeys(withImplicitOrder(query));
  return (a, b) => {
    for (const k of keys) {
      const c = cmp(fieldOf(a, k.field), fieldOf(b, k.field)) ?? 0;
      if (c) return k.dir === "desc" ? -c : c;
    }
    return 0;
  };
}

/** Could this doc be in the query's results, ignoring limit/limitToLast/offset? */
export function matchesQuery(d: DocOut, q: Query): boolean {
  return runLocal([d], { ...q, limit: undefined, limitToLast: undefined, offset: undefined }).length === 1;
}

/** Runs a query over documents in memory, with Firestore/Estra semantics. */
export function runLocal(docs: Iterable<DocOut>, query: Query): DocOut[] {
  const q = withImplicitOrder(query);
  let out = [...docs].filter((d) => inCollection(d, q) && matchesFilter(d, q.where));
  const orders = q.orderBy ?? [];
  for (const o of orders) if (o.field !== "id" && o.field !== "__name__") out = out.filter((d) => getPath(d.data, o.field) !== undefined && getPath(d.data, o.field) !== null);
  if (q.near) {
    const n = q.near;
    out = out.flatMap((d) => {
      const g = getPath(d.data, n.field) as { $geo?: [number, number] } | undefined;
      if (!g?.$geo) return [];
      const dist = haversineMi(n.lat, n.lng, g.$geo[0], g.$geo[1]);
      return dist <= n.radiusMi ? [{ ...d, distanceMi: Math.round(dist * 100) / 100 }] : [];
    });
  }
  const keys = orderKeys(q);
  const sortKey = orderComparator(q);
  if (q.near && !orders.length) out.sort((a, b) => (a.distanceMi ?? 0) - (b.distanceMi ?? 0));
  else out.sort(sortKey);
  // Where a doc sits against a cursor in result order: <0 before it, 0 on it, >0 past it.
  const against = (d: DocOut, cursor: unknown[]) => {
    for (let i = 0; i < cursor.length; i++) {
      const k = keys[i]!;
      const c = cmp(fieldOf(d, k.field), cursor[i]) ?? 0;
      if (c) return k.dir === "desc" ? -c : c;
    }
    return 0;
  };
  const start = q.startAfter ?? q.startAt;
  if (start) out = out.filter((d) => (q.startAt ? against(d, start) >= 0 : against(d, start) > 0));
  const end = q.endBefore ?? q.endAt;
  if (end) out = out.filter((d) => (q.endAt ? against(d, end) <= 0 : against(d, end) < 0));
  if (q.offset) out = out.slice(q.offset);
  if (q.limitToLast !== undefined) return out.slice(Math.max(0, out.length - q.limitToLast));
  return q.limit !== undefined ? out.slice(0, q.limit) : out;
}
